import { deepEquals } from '@jack-henry/frontend-utils/functions';
import { toOrdinalSuffix } from '@treasury/utils/functions';
import { isValid } from 'date-fns';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmBaseComponent } from '../tm-base.component';
import { SelectItemVm, TmValueChanged } from './forms';
import { TmDatePicker } from './tm-date-picker';
import { summarizeFrequency } from './tm-frequency.summarizer';
import {
    type Frequency,
    FrequencyDay,
    FrequencyType,
    FrequencyTypeDescriptions,
    FrequencyTypeLabels,
    type MonthlyFrequency,
    type TwiceMonthlyFrequency,
    type WeeklyFrequency,
} from './tm-frequency.types';

import './tm-checkbox';
// eslint-disable-next-line no-duplicate-imports
import './tm-date-picker';
import './tm-select';
import './tm-switch';

export const tagName = 'tm-frequency';
@customElement(tagName)
export class TmFrequency extends TmBaseComponent {
    @property({ type: Object })
    public frequency?: Frequency;

    @property()
    public dateLabel = 'Payment Date';

    @property({ type: Boolean, reflect: true })
    public readOnly = false;

    @property({ type: Boolean, reflect: true })
    public disabled = false;

    @property({ attribute: false })
    public isDateDisabled?: object;

    @property({ attribute: false })
    public minDate = new Date();

    @property({ attribute: false })
    public maxStartDate?: Date;

    @state()
    private isRecurring = false;

    @state()
    private type?: FrequencyType = FrequencyType.OneTime;

    @state()
    private selectedFrequency?: FrequencyType;

    @state()
    private oneTimeDate?: Date;

    @state()
    private startDate?: Date;

    @state()
    private endDate?: Date;

    @state()
    private perpetual?: boolean;

    @state()
    private repeatOnWeekday?: FrequencyDay;

    @state()
    private repeatOnDay?: number;

    @state()
    private repeatOnDay2?: number | null;

    @state()
    private repeatOnLastBusinessDay?: boolean;

    private invalidInputs: string[] = [];

    private readonly startOnDatePickerRef = createRef<TmDatePicker>();

    private get frequenciesForSelect() {
        const frequencies = [];
        for (const frequency of Object.keys(FrequencyType)) {
            if (frequency !== 'OneTime') {
                frequencies.push({
                    label: FrequencyTypeLabels[frequency as FrequencyType],
                    value: frequency,
                });
            }
        }
        return frequencies;
    }

    private get weekdayItems(): SelectItemVm<string>[] {
        const dayKeys = Object.keys(FrequencyDay).filter(label => Number.isNaN(Number(label)));
        const dayValues = Object.values(FrequencyDay).filter(value => !Number.isNaN(Number(value)));
        return dayKeys.map((label, i) => ({
            label,
            value: dayValues[i].toString(),
        }));
    }

    private get dayNumberItems() {
        const days = [];
        for (let index = 1; index <= 31; index++) {
            days.push({
                label: `${toOrdinalSuffix(index)}`,
                value: `${index}`,
                ...(this.repeatOnDay2 && this.repeatOnDay2 <= index && { disabled: true }),
            });
        }
        return days;
    }

    private get day2NumberItems() {
        const days = [];
        for (let index = 1; index <= 31; index++) {
            days.push({
                label: `${toOrdinalSuffix(index)}`,
                value: `${index}`,
                ...(this.repeatOnDay && this.repeatOnDay >= index && { disabled: true }),
            });
        }
        return days;
    }

    private get frequencyDefinition() {
        switch (this.type) {
            case FrequencyType.EverySixMonths:
            case FrequencyType.Quarterly:
            case FrequencyType.Yearly:
                return FrequencyTypeDescriptions[this.type];
            default:
                return '';
        }
    }

    private get summary() {
        if (this.frequency) {
            return summarizeFrequency(this.frequency);
        }

        return '';
    }

    protected firstUpdated(): void {
        if (this.frequency) {
            this.parseFrequency();
        }
    }

    protected updated(changedProperties: Map<PropertyKey, unknown>): void {
        if (!this.readOnly) {
            if (!changedProperties.has('frequency')) {
                this.validateInputs();
                if (!this.invalidInputs.length) {
                    this.dispatchEvent(
                        new CustomEvent('selection', {
                            detail: this.generateFrequency(),
                            bubbles: true,
                        })
                    );
                } else {
                    this.dispatchEvent(
                        new CustomEvent('selection', { detail: false, bubbles: true })
                    );
                }
            }
            if (
                changedProperties.has('frequency') &&
                !!this.frequency &&
                !deepEquals(changedProperties.get('frequency'), this.frequency)
            ) {
                this.parseFrequency();
            }
        }
    }

    protected render() {
        if (this.readOnly) {
            return this.renderSummary();
        }
        return html`
            <tm-switch
                horizontal
                label="Recurring"
                .checked=${this.isRecurring}
                @checked-changed=${() => {
                    this.isRecurring = !this.isRecurring;
                    this.type = this.isRecurring ? this.selectedFrequency : FrequencyType.OneTime;
                }}
                .disabled=${this.disabled}
                class="w-full mb-4"
            ></tm-switch>
            ${this.renderDateOrFrequency()}
        `;
    }

    private parseFrequency() {
        if (!this.frequency) {
            return;
        }

        if (this.frequency.type) {
            this.type = this.frequency.type;
        }

        if (this.frequency.type !== FrequencyType.OneTime) {
            this.isRecurring = true;
            this.selectedFrequency = this.type;

            if (this.frequency.startDate) {
                this.startDate = this.frequency.startDate;
            }

            if (this.frequency.endDate) {
                this.endDate = this.frequency.endDate;
            }

            if (this.frequency.perpetual) {
                this.perpetual = this.frequency.perpetual;
            }

            switch (this.frequency.type) {
                case FrequencyType.Weekly:
                    if ('repeatOn' in this.frequency) {
                        this.repeatOnWeekday = this.frequency.repeatOn as FrequencyDay;
                    }
                    break;
                case FrequencyType.Monthly:
                case FrequencyType.TwiceAMonth:
                    if ('repeatOnLastBusinessDay' in this.frequency) {
                        this.repeatOnLastBusinessDay = this.frequency.repeatOnLastBusinessDay;
                    }
                    if ('repeatOn' in this.frequency) {
                        if (Array.isArray(this.frequency.repeatOn)) {
                            [this.repeatOnDay, this.repeatOnDay2] = this.frequency.repeatOn;
                        } else {
                            this.repeatOnDay = this.frequency.repeatOn as number;
                        }
                    }
                    break;
                default:
                    break;
            }
        } else {
            if (this.frequency.startDate) {
                this.oneTimeDate = this.frequency.startDate;
            }
        }
    }

    private validateInputs() {
        this.invalidInputs = [];

        if (this.isRecurring) {
            if (this.type === FrequencyType.OneTime) {
                this.invalidInputs.push('selectedFrequency');
            }

            const startOnDatepicker = this.startOnDatePickerRef.value;
            if (this.startDate && startOnDatepicker && !startOnDatepicker.isValid) {
                this.invalidInputs.push('startDate');
            }

            if (!this.startDate || !isValid(new Date(this.startDate))) {
                this.invalidInputs.push('startDate');
            }

            if (!this.perpetual && (!this.endDate || !isValid(new Date(this.endDate)))) {
                this.invalidInputs.push('endDate');
            }

            if (this.startDate && this.endDate) {
                const startDate = new Date(this.startDate);
                const endDate = new Date(this.endDate);
                if (startDate.getTime() >= endDate.getTime()) {
                    this.invalidInputs.push('startDate', 'endDate');
                }
                const minEndDate = this.getMinEndDate();
                if (minEndDate && endDate.getTime() < new Date(minEndDate).getTime()) {
                    this.invalidInputs.push('endDate');
                }
            }

            switch (this.type) {
                case FrequencyType.Weekly:
                case FrequencyType.EveryTwoWeeks:
                    if (!this.repeatOnWeekday) {
                        this.invalidInputs.push('repeatOnWeekday');
                    }
                    break;
                case FrequencyType.Monthly:
                    if (!this.repeatOnLastBusinessDay && !this.repeatOnDay) {
                        this.invalidInputs.push('repeatOnDay');
                    }
                    break;
                case FrequencyType.TwiceAMonth:
                    if (!this.repeatOnDay) {
                        this.invalidInputs.push('repeatOnDay');
                    }
                    if (!this.repeatOnDay2 && !this.repeatOnLastBusinessDay) {
                        this.invalidInputs.push('repeatOnDay2');
                    }
                    if (
                        !this.repeatOnLastBusinessDay &&
                        this.repeatOnDay &&
                        this.repeatOnDay2 &&
                        this.repeatOnDay >= this.repeatOnDay2
                    ) {
                        this.invalidInputs.push('repeatOnDay', 'repeatOnDay2');
                    }
                    break;
                default:
                    break;
            }
        } else {
            if (this.type !== FrequencyType.OneTime) {
                this.invalidInputs.push('selectedFrequency');
            }

            if (!this.oneTimeDate || !isValid(new Date(this.oneTimeDate))) {
                this.invalidInputs.push('oneTimeDate');
            }
        }
    }

    private generateFrequency(): Frequency {
        let frequency = {
            type: this.type as FrequencyType,
            startDate: new Date(),
        };

        if (this.type === FrequencyType.OneTime) {
            frequency = {
                ...frequency,
                ...{ startDate: this.oneTimeDate ? new Date(this.oneTimeDate) : new Date() },
            };
        } else {
            frequency = {
                ...frequency,
                ...{ startDate: this.startDate ? new Date(this.startDate) : new Date() },
                ...(this.endDate && {
                    endDate: new Date(this.endDate),
                }),
                ...(this.perpetual && { perpetual: this.perpetual }),
            };
        }

        switch (this.type) {
            case FrequencyType.Weekly:
            case FrequencyType.EveryTwoWeeks:
                return {
                    ...frequency,
                    repeatOn: this.repeatOnWeekday,
                } as WeeklyFrequency;

            case FrequencyType.Monthly:
            case FrequencyType.TwiceAMonth:
                return {
                    ...frequency,
                    repeatOn:
                        this.type === FrequencyType.Monthly
                            ? this.repeatOnDay
                            : [this.repeatOnDay, this.repeatOnDay2],
                    ...(this.repeatOnLastBusinessDay && {
                        repeatOnLastBusinessDay: this.repeatOnLastBusinessDay,
                    }),
                } as MonthlyFrequency | TwiceMonthlyFrequency;
            default:
                return frequency;
        }
    }

    private getMinEndDate() {
        if (!this.startDate || !isValid(new Date(this.startDate))) {
            return undefined;
        }

        const startDate = new Date(this.startDate);
        const minEndDate = new Date(startDate);
        switch (this.type) {
            case FrequencyType.Weekly:
                minEndDate.setDate(startDate.getDate() + 7);
                break;
            case FrequencyType.EveryTwoWeeks:
                minEndDate.setDate(startDate.getDate() + 14);
                break;
            case FrequencyType.Monthly:
            case FrequencyType.TwiceAMonth:
                minEndDate.setMonth(startDate.getMonth() + 1);
                break;
            case FrequencyType.Quarterly:
                minEndDate.setMonth(startDate.getMonth() + 3);
                break;
            case FrequencyType.EverySixMonths:
                minEndDate.setMonth(startDate.getMonth() + 6);
                break;
            case FrequencyType.Yearly:
                minEndDate.setFullYear(startDate.getFullYear() + 1);
                break;
            default:
                break;
        }
        return minEndDate;
    }

    private renderDatePicker() {
        return html`
            <tm-date-picker
                .label=${this.dateLabel}
                required
                .min=${this.minDate}
                .max=${this.maxStartDate}
                .isDateDisabled=${this.isDateDisabled}
                .initial=${this.oneTimeDate}
                @value-changed=${(e: TmValueChanged<Date>) => {
                    const { value, isValid } = e.detail;

                    if (!isValid) {
                        return;
                    }

                    this.oneTimeDate = value;
                }}
                .disabled=${this.disabled}
                class="w-full"
            ></tm-date-picker>
        `;
    }

    private renderFrequencySelect() {
        return html`
            <tm-select
                label="Frequency"
                required
                .items=${this.frequenciesForSelect}
                .initial=${this.selectedFrequency ?? ''}
                @value-changed=${(e: CustomEvent) => {
                    this.selectedFrequency = e.detail.value as FrequencyType;
                    this.type = this.selectedFrequency;
                }}
                .disabled=${this.disabled}
                class="w-full"
            ></tm-select>
            ${this.renderDefinition()}
        `;
    }

    private renderDateOrFrequency() {
        if (this.isRecurring) {
            return [this.renderFrequencySelect(), this.renderFrequencyForm()];
        }
        return this.renderDatePicker();
    }

    private renderFrequencyForm() {
        switch (this.type) {
            case FrequencyType.Weekly:
            case FrequencyType.EveryTwoWeeks:
                return this.renderWeekly();
            case FrequencyType.Monthly:
                return this.renderMonthly();
            case FrequencyType.TwiceAMonth:
                return this.renderTwiceAMonth();
            case FrequencyType.EverySixMonths:
            case FrequencyType.Quarterly:
            case FrequencyType.Yearly:
                return this.renderStartEnd();
            default:
                return nothing;
        }
    }

    private renderStartOnDate() {
        return html`
            <tm-date-picker
                ${ref(this.startOnDatePickerRef)}
                label="Start on"
                required
                .min=${this.minDate}
                .max=${this.maxStartDate}
                .isDateDisabled=${this.isDateDisabled}
                .initial=${this.startDate}
                @value-changed=${(e: TmValueChanged<Date>) => {
                    const { value, isValid } = e.detail;

                    if (!isValid) {
                        return;
                    }

                    this.startDate = value;
                }}
                .disabled=${this.disabled}
                class="w-full"
            ></tm-date-picker>
        `;
    }

    private renderEndOnDate() {
        this.getMinEndDate();
        return html` <tm-date-picker
                label="End on"
                .required=${!this.perpetual}
                .disabled=${this.perpetual || this.disabled}
                .min=${this.getMinEndDate()}
                .isDateDisabled=${this.isDateDisabled}
                .initial=${this.endDate}
                @value-changed=${(e: TmValueChanged<Date>) => {
                    const { value, isValid } = e.detail;

                    if (!isValid) {
                        return;
                    }
                    this.endDate = value;
                }}
                class="w-full"
            ></tm-date-picker>
            <tm-checkbox
                label="No end"
                .checked=${this.perpetual}
                @checked-changed=${(e: CustomEvent) => {
                    this.perpetual = e.detail.value;
                    if (this.perpetual) {
                        this.endDate = undefined;
                    }
                }}
                .disabled=${this.disabled}
                class="w-full"
            ></tm-checkbox>`;
    }

    private renderRepeatOnWeekday() {
        return html` <tm-select
            label="Repeat on"
            required
            .items=${this.weekdayItems}
            .initial=${this.repeatOnWeekday ? this.repeatOnWeekday.toString() : ''}
            @value-changed=${(e: TmValueChanged<string>) => {
                const { value } = e.detail;
                if (value) {
                    this.repeatOnWeekday = parseInt(value);
                }
            }}
            .disabled=${this.disabled}
            class="w-full"
        ></tm-select>`;
    }

    private renderRepeatOnDay() {
        return html` <tm-select
                label="Repeat on"
                .required=${!(this.type === FrequencyType.Monthly && this.repeatOnLastBusinessDay)}
                .disabled=${(this.type === FrequencyType.Monthly && this.repeatOnLastBusinessDay) ||
                this.disabled}
                .items=${this.dayNumberItems}
                .initial=${this.repeatOnDay?.toString()}
                @value-changed=${(e: TmValueChanged<string>) => {
                    const { value } = e.detail;
                    if (value) {
                        this.repeatOnDay = parseInt(value);
                    }
                }}
                class="w-full"
            ></tm-select>
            ${this.type === FrequencyType.Monthly ? this.renderRepeatOnLastBusinessDay() : nothing}`;
    }

    private renderRepeatOnDay2() {
        return html` <tm-select
                label="and"
                .disabled=${this.repeatOnLastBusinessDay || this.disabled}
                .items=${this.day2NumberItems}
                .initial=${this.repeatOnDay2?.toString()}
                @value-changed=${(e: TmValueChanged<string>) => {
                    const { value } = e.detail;
                    if (value) {
                        this.repeatOnDay2 = parseInt(value);
                    }
                }}
                class="w-full"
            ></tm-select>
            ${this.renderRepeatOnLastBusinessDay()}`;
    }

    private renderRepeatOnLastBusinessDay() {
        return html` <tm-checkbox
            label="Last business day"
            .checked=${this.repeatOnLastBusinessDay}
            @checked-changed=${(e: CustomEvent) => {
                this.repeatOnLastBusinessDay = e.detail.value;
                if (this.repeatOnLastBusinessDay) {
                    switch (this.type) {
                        case FrequencyType.TwiceAMonth:
                            this.repeatOnDay2 = undefined;
                            break;
                        case FrequencyType.Monthly:
                            this.repeatOnDay = undefined;
                            break;
                        default:
                            break;
                    }
                }
            }}
            .disabled=${this.disabled}
            class="w-full"
        ></tm-checkbox>`;
    }

    private renderWeekly() {
        return html`
            ${this.renderRepeatOnWeekday()} ${this.renderStartOnDate()} ${this.renderEndOnDate()}
        `;
    }

    private renderTwiceAMonth() {
        return html`
            ${this.renderRepeatOnDay()} ${this.renderRepeatOnDay2()} ${this.renderStartOnDate()}
            ${this.renderEndOnDate()}
        `;
    }

    private renderMonthly() {
        return html`
            ${this.renderRepeatOnDay()} ${this.renderStartOnDate()} ${this.renderEndOnDate()}
        `;
    }

    private renderDefinition() {
        if (!this.frequencyDefinition) {
            return nothing;
        }
        return html` <div class="text-sm">${this.frequencyDefinition}</div> `;
    }

    private renderStartEnd() {
        return html` ${this.renderStartOnDate()} ${this.renderEndOnDate()} `;
    }

    /* We can set this up to a standard layout format once we migrate all of the jhd-data-field to a tm presentation version */
    private renderSummary() {
        return html`<label
                id="readonly-label"
                for="frequencySummary"
                class="pt-1 pr-4 pb-2 -mt-1 text-secondary leading-none overflow-hidden whitespace-nowrap text-ellipsis max-w-full"
                >Frequency</label
            >${this.summary}`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                }

                :host([readonly]) {
                    padding-top: var(--lumo-space-m);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmFrequency;
    }
}
